import maputilities from '@smarttransit/map-utilities'

function escapeHtml (value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function relationToObject (item, relationName) {
  if (!item) return null
  if (typeof item[relationName] === 'function') return item[relationName]()
  return item[relationName] || null
}

function compactUnique (values) {
  return values.filter((value, index) => value && values.indexOf(value) === index)
}

function isCoordinatePair (coordinate) {
  if (!Array.isArray(coordinate)) return false
  if (coordinate.length < 2) return false
  return !Number.isNaN(Number(coordinate[0])) && !Number.isNaN(Number(coordinate[1]))
}

function appendCoordinateSet (allCoordinates, set) {
  if (!set) return allCoordinates
  if (isCoordinatePair(set)) return allCoordinates.concat([set])
  return allCoordinates.concat(set.filter((coordinate) => isCoordinatePair(coordinate)))
}

export function getTripTransportationProfile (trip) {
  return relationToObject(trip, 'transportationProfile')
}

export function getTripLicencePlate (trip) {
  const transportationProfile = getTripTransportationProfile(trip)
  return transportationProfile && transportationProfile.licensePlateNumber
    ? transportationProfile.licensePlateNumber
    : ''
}

export function getUniqueTripLicencePlates (trips) {
  return compactUnique((trips || []).map((trip) => getTripLicencePlate(trip)))
}

export function normalizeGeoPoint (point) {
  if (!point) return null
  const lng = Number(point.lng !== undefined ? point.lng : point.long)
  const lat = Number(point.lat)
  if (Number.isNaN(lng) || Number.isNaN(lat)) return null
  return { lng, lat }
}

// City maps hide bus stops below zoom 13. Trip-detail stops belong to the vehicle
// route being inspected, so they stay visible at the fitted trip zoom.
export const TRIP_BUS_STOPS_MIN_ZOOM = 0

function trimRouteIdList (routeIdList) {
  if (typeof routeIdList !== 'string') return ''
  return routeIdList.trim()
}

function parseRouteIdListObject (routeIdList) {
  if (!routeIdList) return null
  try {
    const parsed = JSON.parse(routeIdList)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (err) {
    return null
  }
}

function hasLatLon (point) {
  return Boolean(point && point.lat !== undefined && point.lon !== undefined)
}

function isCompleteRouteIdList (routeIdList) {
  const parsed = parseRouteIdListObject(routeIdList)
  if (!parsed) return false
  return hasLatLon(parsed.from) && hasLatLon(parsed.to)
}

export function getTripBusStopRouteIdList (trip) {
  if (!trip) return null
  const routeIdList = trimRouteIdList(trip.transportationRouteIdList)
  if (!isCompleteRouteIdList(routeIdList)) return null
  return routeIdList
}

function isFiniteNumber (value) {
  if (value === null || value === undefined || value === '') return false
  return !Number.isNaN(Number(value))
}

function lngLatFromPair (lon, lat) {
  if (!isFiniteNumber(lon) || !isFiniteNumber(lat)) return null
  return { lon: Number(lon), lat: Number(lat) }
}

function lngLatFromLonglat (longlat) {
  if (!Array.isArray(longlat) || longlat.length < 2) return null
  return lngLatFromPair(longlat[0], longlat[1])
}

function readBusStopLngLat (busStop) {
  if (!busStop) return null
  return lngLatFromPair(busStop.lon, busStop.lat) || lngLatFromLonglat(busStop.longlat)
}

export function toMapboxBusStop (busStop) {
  const lngLat = readBusStopLngLat(busStop)
  if (!lngLat) return null
  return Object.assign({}, busStop, lngLat)
}

export function toMapboxBusStops (busStops) {
  return (busStops || []).map(toMapboxBusStop).filter(Boolean)
}

export function routeGeometryToCoordinates (routeGeometry) {
  if (!Array.isArray(routeGeometry) || !routeGeometry.length) return []

  return routeGeometry.reduce((coordinates, polyline) => {
    if (!polyline) return coordinates
    try {
      return coordinates.concat(maputilities.convertPolylineToCoordinates(polyline) || [])
    } catch {
      return coordinates
    }
  }, [])
}

export function formatTripOptionLabel (trip, index) {
  const tripNumber = index + 1
  const label = [trip && trip.originLabel, trip && trip.destinationLabel].filter(Boolean).join(' to ')
  return label ? `Trip ${tripNumber}: ${label}` : `Trip ${tripNumber}`
}

export function getTripFareAmount ({ transaction, trip, trips }) {
  const linkedTrips = trips || []
  const sourceAmount = linkedTrips.length === 1 && transaction ? transaction.amount : (trip && trip.cost)
  return Math.abs(Number(sourceAmount) || 0)
}

export function getRouteCoordinatesDistance (coordinates) {
  if (!coordinates.length) return null
  return maputilities.distanceTravelled(coordinates, coordinates[coordinates.length - 1], coordinates[0])
}

export function formatDistanceLabel (distance) {
  if (distance === null || distance === undefined || distance === '' || distance === false) return 'N/A'
  const numericDistance = Number(distance)
  if (Number.isNaN(numericDistance)) return 'N/A'
  return `${Number(numericDistance.toFixed(2))} km`
}

export function buildTripPopupHtml ({ title, fields }) {
  const fieldHtml = (fields || [])
    .filter((field) => field && field.value)
    .map((field) => `<div><strong>${escapeHtml(field.label)}:</strong> ${escapeHtml(field.value)}</div>`)
    .join('')

  return `<div class="passenger-trip-popup"><strong>${escapeHtml(title)}</strong>${fieldHtml}</div>`
}

export function fitMapBounds (mapboxInstance, coordinateSets) {
  const currentMap = mapboxInstance && mapboxInstance.currentMap
  const coordinates = (coordinateSets || []).reduce(appendCoordinateSet, [])

  if (!currentMap || !coordinates.length) return

  const lngs = coordinates.map((coordinate) => Number(coordinate[0]))
  const lats = coordinates.map((coordinate) => Number(coordinate[1]))
  // `animate: false` makes the camera jump straight to the route bounds. An animated
  // fly-over from the map's default world view to the route smears the route/line
  // layers while tiles are still loading (cold cache); an instant jump avoids that.
  currentMap.fitBounds([
    [Math.min.apply(Math, lngs), Math.min.apply(Math, lats)],
    [Math.max.apply(Math, lngs), Math.max.apply(Math, lats)]
  ], { padding: 40, maxZoom: 15, animate: false })
}
